/**
 * Motor genérico para leer el valor REAL de un KPI (sitio + año), reusando
 * las mismas APIs y la misma fórmula que ya usa cada dashboard de KPIs
 * (Energía, Agua, Residuos, Efluentes, Vehículos), sin tocar esos componentes
 * ni duplicar sus fórmulas — cada módulo solo aporta su config (catálogos,
 * fórmula real, clases de API) y este motor hace la agregación una sola vez.
 */
export async function computeRealKpiValue({
  accessToken,
  site,
  year,
  fieldKey,
  rawFields,
  formulasFn,
  convertFn,
  getRecordsFn,
  recordsWrapperKey,
  getUnitsFn,
  getBaselineFn,
  getWorkersFn,
  getConfigFn,
  moduleName,
  needsBaseline = true,
  needsEmployees = true,
}) {
  if (!accessToken || !site || !year) return { available: false, reason: "missing_params" };

  try {
    const [recordsRes, baselineRes, workersRes, unitsRes, configRes] = await Promise.allSettled([
      getRecordsFn(accessToken, site, [year]),
      needsBaseline ? getBaselineFn(accessToken, site, year) : Promise.resolve(null),
      needsEmployees ? getWorkersFn(accessToken, site, [year]) : Promise.resolve(null),
      getUnitsFn(accessToken, site, year, rawFields),
      getConfigFn(accessToken, site, moduleName, true),
    ]);

    const records = recordsRes.status === "fulfilled" ? recordsRes.value?.[recordsWrapperKey]?.data || [] : [];
    if (!records.length) return { available: false, reason: "no_data" };

    const baseline = baselineRes.status === "fulfilled" ? baselineRes.value?.total || 1 : 1;
    const employees = workersRes.status === "fulfilled" ? workersRes.value?.totalWorkers?.[year] || 1 : 1;
    const autoUnits = unitsRes.status === "fulfilled" ? unitsRes.value?.fields || {} : {};

    // Unidad configurada a mano por el sitio tiene prioridad sobre la unidad
    // "más usada" auto-detectada — mismo criterio que usa cada dashboard.
    const configForm = configRes.status === "fulfilled" ? configRes.value?.[0] : null;
    const configUnits = {};
    (configForm?.fields || []).forEach((field) => {
      if (field.name && field.unit) configUnits[field.name] = field.unit;
    });

    // Suma cruda de todos los campos presentes en los registros de ese año,
    // convertidos a la unidad "oficial" del campo, acotado a un solo año.
    const totalsByField = {};
    records
      .filter((r) => String(r.year) === String(year))
      .forEach((record) => {
        rawFields.forEach((field) => {
          const raw = record[field];
          if (!raw || raw.value === undefined || raw.value === null || !raw.unit) return;
          const targetUnit = configUnits[field] || autoUnits[field] || raw.unit;
          const converted = convertFn(raw.value, raw.unit, targetUnit, field) || 0;
          if (!totalsByField[field]) totalsByField[field] = { total: 0, unit: targetUnit };
          totalsByField[field].total += converted;
        });
      });

    // La fórmula real lee data[key]?.[year].total (sin '?.' antes de .total)
    // -> hay que garantizar la entrada para TODOS los campos crudos posibles,
    // no solo los que tuvieron datos este año, o revienta.
    const dataForFormula = {};
    rawFields.forEach((field) => {
      dataForFormula[field] = { [year]: totalsByField[field] || { total: 0, unit: undefined } };
    });

    const result = formulasFn(fieldKey, year, dataForFormula, baseline || 1, employees || 1);
    if (!result || result.total === undefined || Number.isNaN(result.total)) {
      return { available: false, reason: "calculation_error" };
    }
    return { available: true, total: result.total, unit: result.unit };
  } catch (error) {
    console.error(`computeRealKpiValue (${moduleName}) error:`, error);
    return { available: false, reason: "error" };
  }
}
